import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Container,
  InputAdornment,
  IconButton,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, Person } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Logo } from '../components/Logo';
import { MagicBackground } from '../components/MagicBackground';
import { useNavigate } from 'react-router-dom';
import { signUp, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Basic validation
      if (!email || !password || !firstName || !lastName) {
        throw new Error('Please fill in all fields');
      }

      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            given_name: firstName,
            family_name: lastName,
            name: `${firstName} ${lastName}`,
          },
        },
      });
      
      console.log('[SIGNUP] Signup result:', result);
      // Show verification code input
      setShowVerification(true);
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!verificationCode || verificationCode.length !== 6) {
        throw new Error('Please enter the 6-digit verification code');
      }

      console.log('[SIGNUP] Confirming signup with code...');
      await confirmSignUp({
        username: email,
        confirmationCode: verificationCode,
      });
      
      console.log('[SIGNUP] Email verified successfully');
      setSuccess(true);
      
      // Redirect to login after successful verification
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      console.error('[SIGNUP] Verification error:', err);
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setError(null);

    try {
      console.log('[SIGNUP] Resending verification code...');
      await resendSignUpCode({ username: email });
      setError(null);
      // Show success message briefly
      const successMsg = 'Verification code resent! Check your email.';
      setError(successMsg);
      setTimeout(() => setError(null), 3000);
    } catch (err: any) {
      console.error('[SIGNUP] Resend error:', err);
      setError(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0a0e27',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(123, 97, 255, 0.15) 0%, rgba(10, 14, 39, 0) 50%)',
          zIndex: 0,
        }
      }}
    >
      {/* Reusable Magic Background with Signup Theme Colors */}
      <MagicBackground color="#7B61FF" blobColors={['rgba(123, 97, 255, 0.2)', 'rgba(0, 102, 255, 0.15)']} />

      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ 
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }}
        >
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Logo size="large" />
            </Box>
            <Typography variant="h4" fontWeight={700} color="white" mb={1}>
              Get Started
            </Typography>
            <Typography variant="body1" color="rgba(255, 255, 255, 0.6)">
              Start your 14-day free trial today
            </Typography>
          </Box>

          <Paper
            elevation={0}
            component="form"
            onSubmit={showVerification ? handleVerification : handleSignup}
            sx={{
              p: 4,
              borderRadius: 4,
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 12px 48px rgba(0, 0, 0, 0.3)',
              }
            }}
          >
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  bgcolor: 'rgba(255, 107, 107, 0.1)', 
                  color: '#FF6B6B', 
                  border: '1px solid rgba(255, 107, 107, 0.2)',
                  '& .MuiAlert-icon': { color: '#FF6B6B' }
                }}
              >
                {error}
              </Alert>
            )}

            {success ? (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 3, 
                  bgcolor: 'rgba(0, 200, 150, 0.1)', 
                  color: '#00C896', 
                  border: '1px solid rgba(0, 200, 150, 0.2)',
                  '& .MuiAlert-icon': { color: '#00C896' }
                }}
              >
                Email verified successfully! Redirecting to login...
              </Alert>
            ) : showVerification ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="h6" color="white" fontWeight={600} mb={1}>
                    Verify Your Email
                  </Typography>
                  <Typography variant="body2" color="rgba(255, 255, 255, 0.6)">
                    We sent a verification code to <strong>{email}</strong>
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      bgcolor: 'rgba(0, 0, 0, 0.2)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#00D4FF' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.5)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#00D4FF' },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || verificationCode.length !== 6}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: 16,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #0066FF 0%, #00D4FF 100%)',
                    boxShadow: '0 8px 20px rgba(0, 102, 255, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0052CC 0%, #00B8E6 100%)',
                      boxShadow: '0 12px 24px rgba(0, 102, 255, 0.4)',
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.3)',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Email'}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={handleResendCode}
                  disabled={resendLoading}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      color: '#00D4FF',
                    },
                  }}
                >
                  {resendLoading ? 'Sending...' : 'Resend Code'}
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        bgcolor: 'rgba(0, 0, 0, 0.2)',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&.Mui-focused fieldset': { borderColor: '#00D4FF' },
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.5)' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#00D4FF' },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        bgcolor: 'rgba(0, 0, 0, 0.2)',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&.Mui-focused fieldset': { borderColor: '#00D4FF' },
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.5)' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#00D4FF' },
                    }}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      bgcolor: 'rgba(0, 0, 0, 0.2)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#00D4FF' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.5)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#00D4FF' },
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      bgcolor: 'rgba(0, 0, 0, 0.2)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#00D4FF' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.5)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#00D4FF' },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: 16,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #0066FF 0%, #00D4FF 100%)',
                    boxShadow: '0 8px 20px rgba(0, 102, 255, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0052CC 0%, #00B8E6 100%)',
                      boxShadow: '0 12px 24px rgba(0, 102, 255, 0.4)',
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.3)',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
                </Button>
              </Box>
            )}

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="rgba(255, 255, 255, 0.6)">
                Already have an account?{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/login')}
                  sx={{ color: '#00D4FF', textDecoration: 'none', fontWeight: 600 }}
                >
                  Sign In
                </Link>
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
}
